<template>
    <div class="max-w-4xl mx-auto w-full space-y-6">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Create Attendance Session
            </h1>
            <button
                @click="goBack"
                class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
                Back
            </button>
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ error }}
                    </p>
                    <button
                        @click="clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Form -->
        <form
            @submit.prevent="handleSubmit"
            class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6"
        >
            <!-- Class Subject Selection -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Class Subject *
                </label>
                <SearchableSelect
                    v-model="form.class_subject_id"
                    :options="classSubjectOptions"
                    placeholder="Select Class Subject"
                    :option-label="getClassSubjectLabel"
                    option-value="id"
                    :required="true"
                    :error="errors.class_subject_id"
                />
                <p
                    v-if="
                        errors.class_subject_id &&
                        errors.class_subject_id.length > 0
                    "
                    class="mt-1 text-sm text-danger-600"
                >
                    {{ errors.class_subject_id[0] }}
                </p>
            </div>

            <!-- Teacher Selection (admin/tu only) -->
            <div v-if="!isTeacher">
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Teacher *
                </label>
                <SearchableSelect
                    v-model="form.teacher_id"
                    :options="teacherOptions"
                    placeholder="Select Teacher"
                    :option-label="getTeacherLabel"
                    option-value="id"
                    :error="errors.teacher_id"
                />
                <p
                    v-if="errors.teacher_id && errors.teacher_id.length > 0"
                    class="mt-1 text-sm text-danger-600"
                >
                    {{ errors.teacher_id[0] }}
                </p>
            </div>

            <!-- Attendance Date -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Attendance Date *
                </label>
                <input
                    v-model="form.attendance_date"
                    type="date"
                    class="form-input w-full"
                    :class="{ 'border-danger-500': errors.attendance_date }"
                />
                <p
                    v-if="
                        errors.attendance_date &&
                        errors.attendance_date.length > 0
                    "
                    class="mt-1 text-sm text-danger-600"
                >
                    {{ errors.attendance_date[0] }}
                </p>
            </div>

            <!-- Mode -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Mode *
                </label>
                <SearchableSelect
                    v-model="form.mode"
                    :options="modeOptions"
                    placeholder="Select Mode"
                    option-label="label"
                    option-value="value"
                    :required="true"
                />
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    QR mode generates a code students enter (or scan) to check
                    in. Manual mode relies on the teacher marking students.
                </p>
            </div>

            <!-- Start Time -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Start Time *
                </label>
                <input
                    v-model="form.start_time"
                    type="datetime-local"
                    class="form-input w-full"
                    :class="{ 'border-danger-500': errors.start_time }"
                />
                <p
                    v-if="errors.start_time && errors.start_time.length > 0"
                    class="mt-1 text-sm text-danger-600"
                >
                    {{ errors.start_time[0] }}
                </p>
            </div>

            <!-- End Time -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    End Time *
                </label>
                <input
                    v-model="form.end_time"
                    type="datetime-local"
                    class="form-input w-full"
                    :class="{ 'border-danger-500': errors.end_time }"
                />
                <p
                    v-if="errors.end_time && errors.end_time.length > 0"
                    class="mt-1 text-sm text-danger-600"
                >
                    {{ errors.end_time[0] }}
                </p>
            </div>

            <!-- Notes -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Notes
                </label>
                <textarea
                    v-model="form.notes"
                    rows="3"
                    placeholder="Optional notes for this session"
                    class="form-input w-full"
                ></textarea>
            </div>

            <!-- Actions -->
            <div class="flex justify-end gap-3">
                <button
                    type="button"
                    @click="goBack"
                    class="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    :disabled="submitting"
                    class="btn btn-primary disabled:opacity-50"
                >
                    {{
                        submitting ? "Creating..." : "Create Attendance Session"
                    }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import SearchableSelect from "@/components/SearchableSelect.vue";
import { useAuthStore } from "@/stores/authStore";
import attendanceApi from "@/api/attendance";
import { classSubjectApi } from "@/api/classSubject";
import { teacherApi } from "@/api/teacher";

const router = useRouter();
const authStore = useAuthStore();

const loading = ref(false);
const submitting = ref(false);
const error = ref<string | null>(null);

const classSubjectOptions = ref<any[]>([]);
const teacherOptions = ref<any[]>([]);

const isTeacher = computed(() => authStore.hasRole("teacher"));

const form = ref({
    class_subject_id: "",
    teacher_id: "",
    attendance_date: new Date().toISOString().slice(0, 10),
    mode: "qr" as "qr" | "manual",
    start_time: "",
    end_time: "",
    notes: "",
});

const errors = ref<Record<string, string[]>>({});

const modeOptions = [
    { label: "QR Code", value: "qr" },
    { label: "Manual", value: "manual" },
];

function goBack() {
    router.push("/attendance");
}

function clearError() {
    error.value = null;
}

function getClassSubjectLabel(option: any) {
    return `${option.class?.grade_level || ""} ${option.class?.name || ""} - ${option.subject?.name || ""} (${option.semester?.name || "-"})`;
}

function getTeacherLabel(option: any) {
    return `${option.user?.name || "-"}${option.nip ? ` (${option.nip})` : ""}`;
}

async function fetchOptions() {
    // Class subjects are essential for this page; the teacher list is not.
    const [classSubjectsResult, teachersResult] = await Promise.allSettled([
        classSubjectApi.getAll({ is_active: true, per_page: 1000 }),
        isTeacher.value
            ? Promise.resolve(null)
            : teacherApi.getAll({ per_page: 1000 }),
    ]);

    if (classSubjectsResult.status === "fulfilled") {
        classSubjectOptions.value =
            classSubjectsResult.value?.data?.items || [];
    } else {
        console.error(classSubjectsResult.reason);
    }

    if (teachersResult.status === "fulfilled" && teachersResult.value) {
        teacherOptions.value = teachersResult.value.data?.items || [];
    } else if (teachersResult.status === "rejected") {
        console.error(teachersResult.reason);
    }
}

async function handleSubmit() {
    errors.value = {};
    submitting.value = true;
    error.value = null;

    try {
        const payload: any = {
            class_subject_id: form.value.class_subject_id,
            attendance_date: form.value.attendance_date,
            mode: form.value.mode,
            start_time: form.value.start_time,
            end_time: form.value.end_time,
        };

        if (!isTeacher.value && form.value.teacher_id) {
            payload.teacher_id = form.value.teacher_id;
        }

        if (form.value.notes) {
            payload.notes = form.value.notes;
        }

        const attendance = await attendanceApi.create(payload);

        router.push(`/attendance/${attendance.id}`);
    } catch (err: any) {
        if (err.response?.status === 422) {
            errors.value = err.response?.data?.errors || {};
            error.value = err.response?.data?.message || "Validation failed";
        } else {
            error.value =
                err.response?.data?.message || "Failed to create attendance";
        }
    } finally {
        submitting.value = false;
    }
}

onMounted(() => {
    fetchOptions();
});
</script>
